import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainDefaultCell, DomainOperation, DomainTld } from '../types/models/DomainPricing';
import type { ApiResponseDomainMatrix } from '../types/api/ApiResponseDomainPricing';

interface UseDomainPricingReturn {
  operations: DomainOperation[];
  tlds: DomainTld[];
  cells: DomainDefaultCell[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Upsert (or remove, when unitPrice is null) one platform-default cell in place from a PUT/DELETE
   * response, so an edit updates its cell without a full refetch.
   */
  applyCell: (slug: string, tld: string, unitPrice: string | null) => void;
}

// GET /admin/products/domains — the platform-default domain pricing matrix (operations × TLDs) plus
// the TLD registry, as three flat arrays. Staff-only on the backend; the page mirrors the gate via
// `enabled`. useApiFetch injects the bearer token (the impersonation header is harmless on this
// staff route — it authorizes on the real user).
export const useDomainPricing = (enabled = true): UseDomainPricingReturn => {
  const [operations, setOperations] = useState<DomainOperation[]>([]);
  const [tlds, setTlds] = useState<DomainTld[]>([]);
  const [cells, setCells] = useState<DomainDefaultCell[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/products/domains');
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseDomainMatrix | null;
      if (body?.status === 'success' && body.data?.operations && body.data?.tlds && body.data?.cells) {
        setOperations(body.data.operations);
        setTlds(body.data.tlds);
        setCells(body.data.cells);
      } else {
        setError(body?.message ?? 'Failed to load the domain pricing matrix');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching domain pricing');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const applyCell = useCallback((slug: string, tld: string, unitPrice: string | null) => {
    setCells((prev) => {
      const others = prev.filter((c) => !(c.slug === slug && c.tld === tld));
      return unitPrice === null ? others : [...others, { slug, tld, unit_price: unitPrice }];
    });
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchMatrix();
    }
  }, [enabled, auth.isAuthenticated, fetchMatrix]);

  return { operations, tlds, cells, loading, error, refetch: fetchMatrix, applyCell };
};
